import {
  Avatar,
  AvatarFallback,
  Button,
  Heading,
  Icon,
  PageTabs,
  useToast,
} from "@elaniintc/goes-design-system";
import { ConfirmationModalBase } from "@/components/common/modals";
import { useMemo, useState, useEffect } from "react";
import { cn } from "@/utils/theme";
import TableHeaderWithTab from "@/components/common/table/header-with-tab";
import { DigitalResolutionTable } from "./components/resolution-table";
import { useLocation, useNavigate, useParams } from "react-router";
import { DigitalRequestTable } from "./components/requests-table";
import { MoreDetailsModal } from "./components/MoreDetailsModal";
import { DetailsHistory } from "../components/DetailsHistory";
import { BadgesSection } from "./components/BadgesSection";
import {
  useDeleteRegistry,
  useGetRegistry,
} from "@/queries/digital-files/registries.api";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { getDefaultDetailsFilters, getSectionsFromRegistry } from "../utils";
import { ResolutionModal } from "./components/ResolutionModal";
import { FiltersModal } from "@/components/ui/filters/FiltersModal";
import { type FilterOption } from "@/types/common/filters";
import { usePageSortFilters } from "@/hooks/use-page-fs";
import ComplexRelationships from "./components/complex-relationships";
import type { ProccesedExpedientValues } from "@/types/entities/templates";
import { Skeleton } from "@/components/ui/skeleton";
import { processValues } from "./utils";
import { buildHierarchyStatusString } from "@/pages/home/utils/serialization";
import { parseAsString, useQueryState } from "nuqs";
import {
  ExpedientRegistryInsolvencies,
  type TypeModalState,
} from "./components/insolvencies/insolvencies";
import { RegistryFormProvider } from "./contexts/RegistryFormContext";
import { useGetInsolvencies } from "@/queries/users/insolvencies.api";
import { DetailsMenu } from "./components/DetailsMenu";
import {
  AssociatesInfo,
  RelatedRegistriesInfo,
  LastUpdateInfo,
} from "./components/RegistryDetailInfo";

type TabPages = "details" | "history";
const pageTabs = [
  {
    label: "Detalle",
    key: "details",
  },
];

type TablePages = "requests" | "resolutions";
const tableTabs = [
  {
    label: "Solicitudes",
    value: "requests",
  },
  {
    label: "Resoluciones",
    value: "resolutions",
  },
];

type RegistryDetailsPageModalState =
  | "idle"
  | "more-details"
  | "delete-registry"
  | "resolution"
  | "filters"
  | TypeModalState;

type RegistryNavigationState = {
  returnTo?: string;
};

const RegistryDetailPage = () => {
  const { hasPermission } = useAuthorization();
  const [activeTab, setActiveTab] = useState<TabPages>("details");
  const [activeTableTab, setActiveTableTab] = useState<TablePages>("requests");
  const [modalState, setModalState] =
    useState<RegistryDetailsPageModalState>("idle");
  const [searchString, setSearchString] = useState("");
  const { registryId: id, id: entityId } = useParams();
  const location = useLocation();
  const returnTo = (location.state as RegistryNavigationState | null)?.returnTo;
  const { mutateAsync: deleteRegistry } = useDeleteRegistry(entityId);
  const { data: insolvenciesData } = useGetInsolvencies("", id ? [id] : [], {
    insolvencyable_type: "expedient_base_registry",
  });

  const hasInsolvencies = insolvenciesData
    ? insolvenciesData.data.length > 0
    : false;

  const canCreate = hasPermission(Permissions.expedient_base_registries.create); // manage canCreate permissions here
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [status, setStatus] = useQueryState(
    "status",
    parseAsString.withDefault(""),
  );

  const defaultFormatFilters = useMemo(
    () => getDefaultDetailsFilters(status, activeTableTab === "resolutions"),
    [status, activeTableTab],
  );

  const { formatFilters, setPage, setFormatFilters, query } =
    usePageSortFilters({
      defaultFormatFilters,
    });

  useEffect(() => {
    setFormatFilters(defaultFormatFilters);
  }, [defaultFormatFilters, setFormatFilters]);

  const { data, isLoading, error } = useGetRegistry(id || "", !!id);
  const isEmpty = !data || !!error;

  const sections = useMemo(() => {
    if (!data) return [];
    return getSectionsFromRegistry(data);
  }, [data]);

  const expirations = useMemo(() => {
    if (!data?.field_values) return {};
    const expirations: Record<string, string> = {};
    data.field_values.forEach((field) => {
      if (field.expiration_at) {
        expirations[field.expedient_base_entity_field_id] = field.expiration_at;
      }
    });
    return expirations;
  }, [data]);

  const [processedValues, setProcessedValues] =
    useState<ProccesedExpedientValues>({});
  const [isLoadingProcessedValues, setIsLoadingProcessedValues] =
    useState(false);

  useEffect(() => {
    const manageProcessedValues = async () => {
      setIsLoadingProcessedValues(true);
      const processedValues = await processValues(data);
      setProcessedValues(processedValues);
      setIsLoadingProcessedValues(false);
    };

    if (data) {
      manageProcessedValues();
    }
  }, [data]);

  const onCloseFilters = (type: string) => {
    if (type !== "clean") {
      setModalState("idle");
      return;
    }
    setFormatFilters(
      getDefaultDetailsFilters("", activeTableTab === "resolutions"),
    );
    setStatus("");
    setModalState("idle");
  };

  const onApplyFilters = (newFilters: FilterOption[]): FilterOption[] => {
    newFilters.forEach((f) => {
      if (f.isUpdated) {
        if (f.type === "hierarchyStatus") {
          setStatus(buildHierarchyStatusString(f));
        }

        setPage(1);
      }
    });

    // Set filter to updated false if result is empty
    const updatedFilters = newFilters.map((f) => {
      if (f.result === "") {
        return {
          ...f,
          isUpdated: false,
          result: "",
        };
      }
      return f;
    });
    return updatedFilters;
  };

  const handleDelete = () => {
    setModalState("delete-registry");
  };

  const confirmDelete = async () => {
    if (!id) return;
    try {
      await deleteRegistry(id);
      showToast({
        title: "Registro eliminado",
        description: "El registro ha sido eliminado correctamente.",
        options: {
          type: "success",
        },
      });
      navigate(`/expedientes`);
    } catch (e) {
      console.error("Error deleting registry:", e);
      showToast({
        title: "Error",
        description: "Hubo un error al eliminar el registro.",
        options: {
          type: "error",
        },
      });
    }
    setModalState("idle");
  };

  if (isLoading || !data) {
    return (
      <div className="w-full">
        <div className="flex flex-col gap-6 w-full min-w-0">
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <Icon
                name="spinner-third"
                type="fas"
                className="animate-spin text-primary text-xl text-on-disabled"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RegistryFormProvider>
      {" "}
      <ConfirmationModalBase
        isOpen={modalState === "delete-registry"}
        onClose={() => setModalState("idle")}
        title="Eliminar registro"
        description="¿Está seguro que desea eliminar este registro? Esta acción no se puede deshacer."
        onAccept={confirmDelete}
        acceptText="Eliminar registro"
        cancelText="Cancelar"
        acceptVariant="danger-primary"
        isLoading={false}
      />
      <div className="flex flex-col gap-6">
        <div className="flex gap-2 items-center border-b border-neutral-silent">
          <Avatar shape="square" size="sm">
            <AvatarFallback
              shape="square"
              color={isEmpty ? "neutral" : "action"}
            >
              <Icon
                type="fas"
                name="folder"
                size="sm"
                className={cn(
                  "text-action-quiet",
                  isEmpty && "text-on-disabled",
                )}
              />
            </AvatarFallback>
          </Avatar>
          <Heading
            variant="h6"
            color="loud"
            className={cn("truncate flex-1", isEmpty && "text-on-disabled")}
          >
            {data?.name ?? ""}
          </Heading>
          <PageTabs
            tabs={pageTabs}
            activeKey={activeTab}
            className="justify-end border-none"
            onTabChange={(key) => setActiveTab(key as TabPages)}
          />
          <DetailsMenu
            entityId={entityId}
            registry={data}
            showInsolvency={hasInsolvencies}
            onDelete={handleDelete}
            onAddInsolvency={() => setModalState("insolvency-form")}
            onViewInsolvencies={() => setModalState("insolvency-list")}
          />
        </div>
        {activeTab === "details" && (
          <>
            <div>
              {sections && data?.field_values_formatted && (
                <>
                  {isLoadingProcessedValues ? (
                    <div className="flex gap-2 flex-wrap">
                      {Array.from({ length: 7 }).map((_, i) => (
                        <Skeleton
                          key={`skeleton-${i + 1}`}
                          className="h-7 w-40"
                        />
                      ))}
                    </div>
                  ) : (
                    <BadgesSection
                      sections={sections}
                      expirations={expirations}
                      processedValues={processedValues}
                      onOpenModal={() => setModalState("more-details")}
                    />
                  )}
                </>
              )}
              <div className="mt-2 flex gap-1 text-body-quiet text-size-body-sm justify-center sm:justify-start">
                <div className="flex gap-1">
                  {data?.unique_code && (
                    <div>
                      ID:{" "}
                      <span className="text-body-loud">
                        {data?.unique_code}
                      </span>{" "}
                      |
                    </div>
                  )}
                  <AssociatesInfo associates={data?.associates} />
                  <span className="w-1 ml-2 border-l border-neutral-silent"></span>
                  <RelatedRegistriesInfo
                    registries={data?.related_registries}
                  />
                  <span className="w-1 ml-2 border-l border-neutral-silent"></span>
                  <LastUpdateInfo data={data} />
                </div>
              </div>
              <div className="mt-2">
                {
                  <ComplexRelationships
                    expedient_base_entity_id={
                      data?.expedient_base_entity_id || ""
                    }
                    expedient_base_registry_id={data?.id || ""}
                    shouldFetch={true}
                  />
                }
              </div>
            </div>
            <ExpedientRegistryInsolvencies
              registry={data}
              modalState={modalState}
              setModalState={setModalState}
            />
            <div>
              <div className="h-full flex flex-col bg-neutral-object border-neutral border-[0.5px] rounded-xl shadow-neutral-md overflow-hidden min-h-[calc(100vh-345px)]">
                {returnTo && (
                  <Button
                    type="button"
                    variant="tertiary"
                    size="default"
                    onClick={() => navigate(returnTo)}
                    className="shrink-0 absolute left-2 bottom-2 z-50 text-action shadow-xl hover:bg-neutral-object active:bg-neutral-object-accent focus:bg-neutral-object"
                  >
                    <Icon type="far" name="arrow-u-turn-up-left" />
                    <span className="hidden sm:inline">
                      Regresar a solicitud
                    </span>
                  </Button>
                )}
                <TableHeaderWithTab
                  tabs={tableTabs}
                  activeTab={activeTableTab}
                  onTabChange={(tab) => {
                    setFormatFilters(
                      getDefaultDetailsFilters(
                        activeTableTab === "resolutions" ? "" : status,
                        activeTableTab !== "resolutions",
                      ),
                    );
                    setSearchString("");
                    setActiveTableTab(tab as TablePages); // NOSONAR typescript:S4325 - tab is typed as string by the onTabChange prop signature, cast is required
                    setStatus("");
                  }}
                  placeholder={
                    activeTableTab === "requests"
                      ? "Buscar solicitud..."
                      : "Buscar resolución..."
                  }
                  canCreate={activeTableTab === "resolutions" && canCreate}
                  createLabel="Agregar Resolución"
                  filtersApplied={
                    formatFilters.filter((f) => f.isUpdated).length
                  }
                  onFilter={() => {
                    setModalState("filters");
                  }}
                  search={searchString}
                  setSearch={(value) => setSearchString(value ?? "")}
                  onCreate={() => setModalState("resolution")}
                />
                {activeTableTab === "requests" ? (
                  <DigitalRequestTable
                    requestList={data?.formality_requests || []}
                    searchString={searchString}
                    queryFilters={{ status, ...query.filter }}
                  />
                ) : (
                  <DigitalResolutionTable
                    registryId={id || ""}
                    searchString={searchString}
                    queryFilters={query.filter}
                  />
                )}
              </div>
            </div>
          </>
        )}
        {activeTab === "history" && id && (
          <DetailsHistory entityId={id} entityType="registry" />
        )}
        {data && (
          <MoreDetailsModal
            entityId={entityId}
            registry={data}
            sections={sections}
            expirations={expirations}
            processedValues={processedValues ?? {}}
            isOpen={modalState === "more-details"}
            onClose={() => setModalState("idle")}
          />
        )}
        <ResolutionModal
          isOpen={modalState === "resolution"}
          onClose={() => setModalState("idle")}
        />
      </div>
      {modalState === "filters" && (
        <FiltersModal
          isOpen={modalState === "filters"}
          onToggle={onCloseFilters}
          hasCurrentFilters={formatFilters.some((filter) => filter.isUpdated)}
          items={formatFilters}
          onApply={(newFilters) => {
            const filters = onApplyFilters(newFilters);
            setFormatFilters(filters);
            setModalState("idle");
          }}
        />
      )}
    </RegistryFormProvider>
  );
};
export { RegistryDetailPage };
